/**
 * AUD-022 (Round 6 polish audit) — React Router's <Routes> performed no
 * focus management on navigation: focus was left wherever the browser's
 * default post-navigation behavior put it (typically <body>), never moved
 * to the new page's content. This moves focus to the new page's <h1>
 * (added sitewide by AUD-016) on every route change — the standard SPA fix,
 * and the natural pairing the audit itself calls out.
 *
 * Several pages (LeadsPage, LeadDetailPage, SettingsPage) render a brief
 * loading state with no <h1> yet before their real content — including its
 * heading — arrives from an async fetch. A plain "focus whatever <h1>
 * exists right after the route changes" would silently no-op on those
 * pages. A MutationObserver watches for the heading to actually appear
 * instead of assuming it's already there when this effect runs.
 *
 * tabIndex=-1 makes an otherwise inert (non-interactive) heading
 * programmatically focusable without adding it to the page's normal Tab
 * order.
 */
import { useEffect } from "react";
import { useLocation } from "react-router-dom";

function focusHeading(heading: HTMLElement) {
  if (!heading.hasAttribute("tabindex")) {
    heading.setAttribute("tabindex", "-1");
  }
  heading.focus();
}

export function RouteFocusManager() {
  const location = useLocation();

  useEffect(() => {
    const existing = document.querySelector("h1");
    if (existing) {
      focusHeading(existing as HTMLElement);
      return;
    }

    const observer = new MutationObserver(() => {
      const heading = document.querySelector("h1");
      if (heading) {
        focusHeading(heading as HTMLElement);
        observer.disconnect();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [location.pathname]);

  return null;
}
